import {
  DATUMSQUELLE_LABELS,
  TASK_STATUS_LABELS,
  alterDerAufgabe,
  ZEITFENSTER,
  ZEITFENSTER_LABELS,
  istDieseWoche,
  meilensteineInWoche,
  montagKurz,
  spalteFuer,
  spalten,
  spaltenLabel,
  spaltenSchluessel,
  wirksamerTermin,
  type PlanungsMeilenstein,
  type PlanungsVorhaben,
  type Termin,
  type Zeitfenster,
} from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { meilensteineQuery, vorhabenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { AufgabenVerweis, useEscSchliesst } from './AufgabeKontext'
import { Card, CardPad } from './ui/primitives'
import { useAufgabenDieserWelt } from '../lib/welt'
import { hintergrundSchliesst } from './ui/hintergrund'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * **Die Zeitachse** – *wer hat was wann vor sich?* (Nils, 19.08.2026,
 * [E-147](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * > *„Ich habe neue Aufgaben in EINGANG. Da muss ich die vorqualifizieren. Dann in Planung
 * > müssen die ja so sauber schon drin sein, das ich da sehe, welcher Entwickler hat was in
 * > seinen Pipelines, welche Aufgabe ist wann dran, usw. Die Zeitfenster bspw. finde ich da
 * > sehr unübersichtlich und nicht hilfreich."*
 *
 * Sie ersetzt die Zeitfenster-Leiste. Die zeigte **nur** Aufgaben, die ein Fenster tragen –
 * am 19.08.2026 **7 von 122** –, kannte kein Datum, keine Person und keine Reihenfolge.
 *
 * ## Personenzeilen sind eine Vorschau, keine Zuteilung
 *
 * Kap. 4.5 verbietet dieser Ansicht ausdrücklich, jemandem Tage zuzuweisen (*Pull statt
 * Push*), und der Kopfkommentar von `Planung.tsx` hielt fest: *„keine Personenzeile"*. Nils hat
 * das am 19.08.2026 auf Nachfrage geöffnet – aber nur als **Vorschau**: hier steht, was jemand
 * **schon hat**, nicht was er tun **soll**. Deshalb gibt es keine Zuteilung per Ziehen, und
 * unzugewiesene Arbeit steht in einer eigenen Zeile **„im Pool – niemand"**, statt auf Köpfe
 * verteilt zu werden. Das Ziehen bleibt der Weg, auf dem eine Aufgabe zu einer Person kommt.
 *
 * ## Warum Zahlen und nicht Titel in den Zellen
 *
 * Zehn Personen mal neun Spalten sind neunzig Zellen. Mit zwei Titeln je Zelle wäre das eine
 * Wand aus Text, in der man die eine dringende Zeile nicht mehr findet. Die Zahl beantwortet
 * *„wo drückt es"*, der Klick beantwortet *„was genau"* – dieselbe Aufteilung wie im Trichter.
 *
 * ## Die Spalte, um die es eigentlich geht
 *
 * **„ohne Datum"** ganz rechts. Dort liegt, was niemand eingeplant hat, und das ist die
 * Arbeitsliste dieser Ansicht. Solange nichts im Zulauf ein Zeitfenster erzeugt, ist sie die
 * längste – und genau das soll man sehen, statt es in einer Leiste zu verstecken, die diese
 * Aufgaben gar nicht erst zeigt.
 */

/** Wie viele Wochen das Raster zeigt. Zwei Stufen, weil „Quartal" und „nächste Woche" beides vorkommt. */
const WOCHEN_STUFEN = [6, 12] as const

type Aufgabe = {
  id: string
  title: string
  status: string
  assigneeId: string | null
  assigneeName: string | null
  dueAt: string | Date | null
  zeitfenster: string | null
  /** Ab wann das Fenster läuft – ohne ihn rutschte die Zeile täglich eine Spalte nach rechts. */
  zeitfensterAb: string | Date | null
  vorhabenId: string | null
  /** Der bediente Meilenstein (E-335) – nur er zieht die Aufgabe an sein Datum. */
  meilensteinId: string | null
  vorhabenLabel: string | null
  groesse: string | null
  /** Für das Alter – `alterDerAufgabe` braucht beide Datumsquellen. */
  sourceCreatedAt: string | Date | null
  createdAt: string | Date
  /** Kommagetrennt aus `tasks.all` – wer für diese Aufgabe in Frage kommt. */
  wunschIds: string | null
}

/**
 * Ein Eintrag in einer Zelle. `moeglich` trennt **fest** von **vorgemerkt**: das eine ist Last,
 * das andere eine Möglichkeit – und die ist **nicht addierbar** (dieselbe Unterscheidung wie die
 * Spalte „möglich" im Leitstand, [E-116](../../../../docs/ENTSCHEIDUNGEN.md)).
 */
type ZeileMitTermin = { aufgabe: Aufgabe; termin: Termin; moeglich: boolean }

export function Zeitachse() {
  /** Die Zeitleiste gehört zum Leitstand und ist gebunden (Z-07, E-235). */
  const { aufgaben } = useAufgabenDieserWelt()
  const { data: vorhaben = [] } = useQuery(vorhabenQuery)
  const { data: meilensteine = [] } = useQuery(meilensteineQuery)
  const [wochen, setWochen] = useState<number>(WOCHEN_STUFEN[0])
  const [offen, setOffen] = useState<{ titel: string; zeilen: ZeileMitTermin[] } | null>(null)

  /**
   * **`heute` einmal je Render**, nicht je Aufgabe: sonst könnte eine Zeile über Mitternacht in
   * eine andere Spalte fallen als die daneben, und die Summen stimmten nicht mehr.
   */
  const heute = useMemo(() => new Date(), [])
  const achse = useMemo(() => spalten(heute, wochen), [heute, wochen])

  const vorhabenNachId = useMemo(
    () =>
      new Map<string, PlanungsVorhaben>(
        vorhaben.map((v) => [
          v.id,
          {
            id: v.id,
            zeitfenster: v.zeitfenster,
            zeitfensterAb: v.zeitfensterAb,
            zieltermin: v.zieltermin,
          },
        ]),
      ),
    [vorhaben],
  )
  const offeneMeilensteine = useMemo<PlanungsMeilenstein[]>(
    () =>
      meilensteine.map((m) => ({
        id: m.id,
        vorhabenId: m.vorhabenId,
        termin: m.termin,
        erreichtAm: m.erreichtAm,
      })),
    [meilensteine],
  )

  /** Offen heißt hier wie überall: nicht erledigt, nicht zurückgestellt. */
  const offeneAufgaben = useMemo(
    () =>
      (aufgaben as unknown as Aufgabe[]).filter(
        (a) => a.status !== 'erledigt' && a.status !== 'zurueckgestellt',
      ),
    [aufgaben],
  )

  /**
   * Aufgabe → Zeile (Person oder „niemand") und Spalte. Einmal gerechnet und in eine Karte
   * gelegt: bei 141 Aufgaben mal 90 Zellen wäre die Suche je Zelle die teuerste Stelle der
   * Ansicht (Leitplanke 4).
   */
  const { zeilen, zellen, eintragNachId } = useMemo(() => {
    const zellen = new Map<string, ZeileMitTermin[]>()
    /**
     * Dieselben Einträge noch einmal nach Aufgabe – **damit ein offenes Popup nicht altert**
     * (21.08.2026). Wer dort ein Zeitfenster setzt, lädt `tasks.all` neu; das Popup hält aber
     * die Zeilen fest, die es beim Klick auf die Zelle bekam, und zeigte danach weiter das alte
     * Fenster **und** das daraus abgeleitete Datum. Über diese Karte schlägt es jede Zeile neu
     * nach – Reihenfolge bleibt, Inhalt wird frisch.
     */
    const eintragNachId = new Map<string, ZeileMitTermin>()
    const namen = new Map<string, string>()
    const nameVon = new Map<string, string>()
    for (const a of offeneAufgaben) {
      if (a.assigneeId !== null && a.assigneeName !== null)
        nameVon.set(a.assigneeId, a.assigneeName)
    }

    const einsortieren = (zeile: string, spalte: string, eintrag: ZeileMitTermin) => {
      const schluessel = `${zeile}|${spalte}`
      const vorhandene = zellen.get(schluessel)
      if (vorhandene) vorhandene.push(eintrag)
      else zellen.set(schluessel, [eintrag])
      eintragNachId.set(eintrag.aufgabe.id, eintrag)
    }

    for (const a of offeneAufgaben) {
      const termin = wirksamerTermin(a, vorhabenNachId, offeneMeilensteine, heute)
      const spalte = spalteFuer(termin, heute, wochen)

      if (a.assigneeId !== null) {
        namen.set(a.assigneeId, a.assigneeName ?? 'Ohne Namen')
        einsortieren(a.assigneeId, spalte, { aufgabe: a, termin, moeglich: false })
        continue
      }

      /**
       * **Vorgemerkt ist nicht dasselbe wie „niemand"** (Nils, 19.08.2026). Eine unzugewiesene
       * Aufgabe mit Namen unter „kommen in Frage" steht **bei diesen Namen** – grau, weil sie
       * niemandem gehört –, und nicht mehr in der Pool-Zeile. Sonst liest sich die Zeile als
       * *„die hat noch keinen, der sie holen könnte"*, und man klickt sie immer wieder auf.
       */
      const kandidaten = (a.wunschIds ?? '').split(',').filter(Boolean)
      if (kandidaten.length === 0) {
        einsortieren('niemand', spalte, { aufgabe: a, termin, moeglich: false })
        continue
      }
      for (const id of kandidaten) {
        namen.set(id, nameVon.get(id) ?? namen.get(id) ?? 'Vorgemerkt')
        einsortieren(id, spalte, { aufgabe: a, termin, moeglich: true })
      }
    }

    const personen = [...namen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'))

    return {
      zeilen: [...personen, { id: 'niemand', name: 'im Pool – niemand vorgemerkt' }],
      zellen,
      eintragNachId,
    }
  }, [offeneAufgaben, vorhabenNachId, offeneMeilensteine, heute, wochen])

  const inhalt = (zeileId: string, spaltenId: string) => zellen.get(`${zeileId}|${spaltenId}`) ?? []

  if (offeneAufgaben.length === 0) return null

  /** Spaltenbreiten: die Beschriftung fest, „ohne Datum" breiter – dort liegt das Meiste. */
  const raster = `170px repeat(${achse.length - 1}, minmax(52px, 1fr)) minmax(78px, 1.4fr)`

  return (
    <>
      <Card className="mb-4">
        <CardPad>
          <div className="mb-2.5 flex flex-wrap items-baseline gap-2 text-[11.5px]">
            <span className="text-muted">Zeitraum:</span>
            {WOCHEN_STUFEN.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWochen(w)}
                aria-pressed={wochen === w}
                className={`cursor-pointer rounded-lg px-2 py-0.5 font-semibold ${
                  wochen === w ? 'bg-ink text-white' : 'border border-line2 text-ink2'
                }`}
              >
                {w} Wochen
              </button>
            ))}
            <span className="ml-auto text-muted">
              Klick auf eine Zahl zeigt die Aufgaben · ◆ Meilenstein
            </span>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              {/* ── Kopfzeile ───────────────────────────────────────────── */}
              <div className="grid items-end gap-px" style={{ gridTemplateColumns: raster }}>
                <div />
                {achse.map((s) => {
                  const jetzt = istDieseWoche(s, heute)
                  const marken = meilensteineInWoche(s, offeneMeilensteine)
                  return (
                    <div
                      key={spaltenSchluessel(s)}
                      className={`px-1 pb-1 text-center text-[10.5px] leading-tight ${
                        jetzt ? 'font-bold text-ink' : 'text-muted'
                      } ${s.art === 'ohne' ? 'font-semibold text-ink2' : ''}`}
                    >
                      {spaltenLabel(s)}
                      {s.art === 'woche' && (
                        <div className="text-[9.5px] opacity-70">{montagKurz(s.montag)}</div>
                      )}
                      {marken.length > 0 && (
                        <div
                          className="text-[10px] text-accent-deep"
                          title={marken.map((m) => m.termin).join(' · ')}
                        >
                          ◆
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* ── Zeilen ──────────────────────────────────────────────── */}
              {zeilen.map((z) => {
                /**
                 * **Die Zeilensumme trennt genauso wie die Zellen.** Eine Summe aus fest und
                 * vorgemerkt behauptet eine Last, die es nicht gibt – und wäre nicht addierbar:
                 * eine Aufgabe mit drei Kandidaten stünde in drei Zeilen (E-116).
                 */
                const alle = achse.flatMap((sp) => inhalt(z.id, spaltenSchluessel(sp)))
                const gesamtFest = alle.filter((d) => !d.moeglich).length
                const gesamtMoeglich = alle.length - gesamtFest
                if (alle.length === 0) return null
                const istPool = z.id === 'niemand'
                return (
                  <div
                    key={z.id}
                    className={`grid items-stretch gap-px ${
                      istPool ? 'mt-1.5 border-t border-line pt-1.5' : ''
                    }`}
                    style={{ gridTemplateColumns: raster }}
                  >
                    <div
                      className={`flex items-center gap-1.5 truncate py-1 pr-2 text-[12px] ${
                        istPool ? 'font-semibold text-accent-deep' : 'text-ink'
                      }`}
                      title={z.name}
                    >
                      <span className="truncate">{z.name}</span>
                      <span className="tabular ml-auto shrink-0 text-[10.5px]">
                        {gesamtFest > 0 && <span className="text-ink2">{gesamtFest}</span>}
                        {gesamtFest > 0 && gesamtMoeglich > 0 && (
                          <span className="mx-[2px] text-muted">·</span>
                        )}
                        {gesamtMoeglich > 0 && (
                          <span className="text-muted italic">{gesamtMoeglich}</span>
                        )}
                      </span>
                    </div>
                    {achse.map((s) => {
                      const drin = inhalt(z.id, spaltenSchluessel(s))
                      const fest = drin.filter((d) => !d.moeglich).length
                      const moeglich = drin.length - fest
                      const leer = drin.length === 0
                      /**
                       * **Drei Farben, drei Aussagen.** Überfälliges ist rot, weil es das
                       * Einzige ist, worauf man sofort reagieren muss; „ohne Datum" bleibt
                       * grau, weil es kein Alarm ist, sondern Arbeit für die Planung.
                       */
                      const ton = leer
                        ? 'bg-[#faf9f7] text-muted'
                        : s.art === 'ueberfaellig'
                          ? 'bg-[#fdeaea] text-pool-sofort'
                          : s.art === 'ohne'
                            ? 'bg-[#f3f2ef] text-ink2'
                            : 'bg-accent-soft text-ink'
                      return (
                        <button
                          key={spaltenSchluessel(s)}
                          type="button"
                          disabled={leer}
                          onClick={() =>
                            setOffen({ titel: `${z.name} · ${spaltenLabel(s)}`, zeilen: drin })
                          }
                          className={`rounded px-1 pt-1 pb-[3px] text-center text-[12px] disabled:cursor-default ${ton} ${
                            leer ? '' : 'cursor-pointer hover:opacity-80'
                          }`}
                          title={
                            leer
                              ? `${z.name} · ${spaltenLabel(s)}: nichts`
                              : `${z.name} · ${spaltenLabel(s)}: ${fest} fest, ${moeglich} vorgemerkt – klicken zeigt die Aufgaben`
                          }
                        >
                          {leer ? (
                            <span className="tabular">·</span>
                          ) : (
                            <>
                              {/**
                               * **Zwei Zahlen, nicht eine.** Nils: *„ggf. dass die dann zwei
                               * Zahlen haben."* Fest ist fett, vorgemerkt kursiv und blass –
                               * die zweite Zahl ist eine Möglichkeit, keine Last.
                               */}
                              <span className="tabular block leading-none">
                                {fest > 0 && <span className="font-semibold">{fest}</span>}
                                {fest > 0 && moeglich > 0 && (
                                  <span className="mx-[2px] text-muted">·</span>
                                )}
                                {moeglich > 0 && (
                                  <span className="text-[11px] text-muted italic">{moeglich}</span>
                                )}
                              </span>
                              {/**
                               * **Der Balken füllt immer die ganze Zelle** – Nils: *„zusammen
                               * ergeben die immer 100 % vom Feld."* Er vergleicht damit nicht
                               * Zellen untereinander (dafür stehen die Zahlen da), sondern
                               * zeigt in einem Blick das **Verhältnis** in dieser einen Zelle:
                               * viel Fettes heißt belegt, viel Blasses heißt nur vorgemerkt.
                               */}
                              <span className="mt-[3px] flex h-[3px] overflow-hidden rounded-full bg-[#e6e4df]">
                                {fest > 0 && (
                                  <i
                                    className="block bg-accent"
                                    style={{ width: `${(fest / drin.length) * 100}%` }}
                                  />
                                )}
                                {moeglich > 0 && (
                                  <i
                                    className="block bg-[#c9c4ba]"
                                    style={{ width: `${(moeglich / drin.length) * 100}%` }}
                                  />
                                )}
                              </span>
                            </>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>

          <p className="mt-2.5 text-[11px] text-muted">
            <strong>Personenzeilen sind eine Vorschau, keine Zuteilung.</strong> Die fette Zahl ist,
            was jemand <strong>fest</strong> hat; die blasse kursive daneben, wofür er nur{' '}
            <em>in Frage kommt</em> – die ist <strong>nicht addierbar</strong>: eine Aufgabe mit
            drei Namen steht in drei Zeilen. Ganz unten bleibt, was <em>niemand</em> vorgemerkt hat.
            Das Datum wird abgeleitet:{' '}
            <strong>Fälligkeit → Meilenstein → Zieltermin → Zeitfenster</strong>, sonst „ohne
            Datum".
          </p>
        </CardPad>
      </Card>

      {offen && (
        <ZellenPopup
          titel={offen.titel}
          zeilen={offen.zeilen}
          frisch={(id) => eintragNachId.get(id)}
          schliessen={() => setOffen(null)}
        />
      )}
    </>
  )
}

/**
 * Der Inhalt einer Zelle – und die Stelle, an der **verschoben** wird.
 *
 * Das Auswahlfeld für das Zeitfenster steht hier und nicht in einem Menü darüber: *„Verschieben
 * eines Zeitfensters ist ein normaler Vorgang"* ist ein Abnahmekriterium der Phase 8
 * ([E-110](../../../../docs/ENTSCHEIDUNGEN.md)), und wer verschieben will, sieht die Aufgabe
 * gerade an. Ohne dieses Feld hätte die neue Ansicht ein Kriterium mitgerissen, das die alte
 * Leiste erfüllte.
 */
function ZellenPopup({
  titel,
  zeilen,
  frisch,
  schliessen,
}: {
  titel: string
  zeilen: ZeileMitTermin[]
  /** Dieselbe Zeile aus dem aktuellen Bestand – siehe `eintragNachId`. */
  frisch: (id: string) => ZeileMitTermin | undefined
  schliessen: () => void
}) {
  const queryClient = useQueryClient()
  /** Esc schließt die Liste – aber erst, wenn kein Aufgabendetail davor steht. */
  useEscSchliesst(schliessen)
  const verschieben = useMutation({
    mutationFn: (v: { id: string; zeitfenster: Zeitfenster | null }) =>
      trpc.tasks.planungSetzen.mutate({ id: v.id, zeitfenster: v.zeitfenster }),
    onSuccess: () => nebenBestandVerwerfen(queryClient),
  })

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[8vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label={titel}
    >
      <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(80vh,100%)] w-full min-w-0 max-w-[760px] flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex items-center gap-2 border-b border-line px-[18px] py-3">
          <h3 className="text-sm font-bold">{titel}</h3>
          <span className="text-[12px] text-muted">{zeilen.length}</span>
          <button
            type="button"
            onClick={schliessen}
            className="ml-auto cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-2.5">
          {zeilen
            .map((z) => frisch(z.aufgabe.id) ?? z)
            .map(({ aufgabe, termin, moeglich }) => (
              <div
                key={aufgabe.id}
                className={`flex flex-wrap items-center gap-2 border-b border-line py-1.5 text-[12.5px] last:border-b-0 ${
                  moeglich ? 'text-ink2 italic' : ''
                }`}
              >
                {/** Dieselbe Unterscheidung wie in der Zelle – sonst sähen beide gleich aus. */}
                {moeglich && (
                  <span
                    className="shrink-0 rounded bg-[#f0efec] px-1.5 text-[10px] not-italic text-muted"
                    title="Kommt in Frage – gehört noch niemandem"
                  >
                    vorgemerkt
                  </span>
                )}
                {/**
                 * **Das Alter steht auch hier** (Nils, 19.08.2026: *„hier müsste noch das Alter
                 * mit rein"* – gesagt zur Trichter-Liste, und es gilt für beide: wer ein
                 * Zeitfenster vergibt, entscheidet mit, was zu lange liegt).
                 */}
                <span
                  className={`tabular w-[42px] shrink-0 text-right text-[11px] ${
                    alterDerAufgabe(aufgabe) > 60 ? 'font-semibold text-pool-sofort' : 'text-muted'
                  }`}
                >
                  {alterDerAufgabe(aufgabe)} T
                </span>
                <span className="w-[80px] shrink-0 truncate text-[11px] text-muted">
                  {TASK_STATUS_LABELS[aufgabe.status as keyof typeof TASK_STATUS_LABELS] ??
                    aufgabe.status}
                </span>
                <AufgabenVerweis
                  /** Die Liste bleibt offen – siehe `useEscSchliesst`. */
                  aufgabeId={aufgabe.id}
                  className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
                  title={aufgabe.title}
                >
                  {aufgabe.title}
                </AufgabenVerweis>
                {/**
                 * **Woher das Datum kommt, steht dran.** „30.09. (Zieltermin des Vorhabens)" ist
                 * eine andere Auskunft als „30.09. (Fälligkeit)" – die eine ist geerbt, die
                 * andere zugesagt.
                 */}
                <span className="shrink-0 text-[11px] text-muted" title={termin?.quelle}>
                  {termin === null
                    ? 'kein Datum'
                    : `${termin.datum.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} · ${DATUMSQUELLE_LABELS[termin.quelle]}`}
                </span>
                <select
                  value={aufgabe.zeitfenster ?? ''}
                  onChange={(e) =>
                    verschieben.mutate({
                      id: aufgabe.id,
                      zeitfenster: (e.target.value || null) as Zeitfenster | null,
                    })
                  }
                  aria-label={`Zeitfenster von ${aufgabe.title}`}
                  className="shrink-0 cursor-pointer rounded border border-line2 bg-surface px-1 text-[10.5px]"
                >
                  <option value="">– kein Fenster</option>
                  {ZEITFENSTER.map((z: Zeitfenster) => (
                    <option key={z} value={z}>
                      {ZEITFENSTER_LABELS[z]}
                    </option>
                  ))}
                </select>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
